import AsyncStorage from '@react-native-async-storage/async-storage';
import { useEffect, useSyncExternalStore } from 'react';
import { logger } from '@/lib/logger';

/**
 * Downloaded videos while ONLINE: the lesson page can either stream the
 * Soundslice player (notation, loops, tools) or play the downloaded file (no
 * data, lock-screen audio). This module holds the user's standing preference
 * and the pure rule that picks the player. Offline there is no choice: the
 * downloaded file is the only source.
 *
 * The preference is persisted in AsyncStorage and mirrored in memory like the
 * registry, so UI can read it synchronously.
 */
export type PlaybackSource = 'download' | 'stream';
export type PlaybackPreference = 'ask' | PlaybackSource;
/**
 * 'choose' = show the "Stream online / Play download" prompt;
 * 'pending' = the stored preference has not been read yet, mount no player.
 */
export type PlaybackDecision = PlaybackSource | 'choose' | 'pending';

const STORAGE_KEY = 'osj.offline.playbackPreference.v1';
const DEFAULT_PREFERENCE: PlaybackPreference = 'ask';

type Listener = () => void;

let preference: PlaybackPreference = DEFAULT_PREFERENCE;
let loaded = false;
let loadPromise: Promise<void> | null = null;
const listeners = new Set<Listener>();

function isPreference(value: unknown): value is PlaybackPreference {
  return value === 'ask' || value === 'download' || value === 'stream';
}

function emit(): void {
  listeners.forEach((l) => {
    try {
      l();
    } catch (err) {
      logger.error('[offline] playback preference listener failed', err);
    }
  });
}

export function loadPlaybackPreference(): Promise<void> {
  if (!loadPromise) {
    loadPromise = (async () => {
      try {
        const raw = await AsyncStorage.getItem(STORAGE_KEY);
        if (isPreference(raw)) preference = raw;
      } catch (err) {
        logger.error('[offline] playback preference load failed', err);
      }
      loaded = true;
      emit();
    })();
  }
  return loadPromise;
}

/** The stored preference, or null until it has been read from disk. */
export function getPlaybackPreference(): PlaybackPreference | null {
  return loaded ? preference : null;
}

export async function setPlaybackPreference(next: PlaybackPreference): Promise<void> {
  preference = next;
  loaded = true;
  emit();
  try {
    await AsyncStorage.setItem(STORAGE_KEY, next);
  } catch (err) {
    logger.error('[offline] playback preference persist failed', err);
  }
}

function subscribe(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function usePlaybackPreference(): PlaybackPreference | null {
  useEffect(() => {
    void loadPlaybackPreference();
  }, []);
  return useSyncExternalStore(subscribe, getPlaybackPreference, () => null);
}

export function playbackPreferenceLabel(value: PlaybackPreference): string {
  switch (value) {
    case 'ask':
      return 'Always ask';
    case 'download':
      return 'Always play download';
    case 'stream':
      return 'Always stream online';
  }
}

export interface PlaybackDecisionInput {
  /** The lesson was hydrated from the local snapshot (no network). */
  offline: boolean;
  /** The current video has a complete, playable download on this device. */
  downloaded: boolean;
  /** It already had one when the video was opened (or the user asked for it). */
  downloadedAtOpen: boolean;
  /** What the user picked during this lesson visit, if anything. */
  visitChoice: PlaybackSource | null;
  /** null until the stored preference has been read. */
  preference: PlaybackPreference | null;
}

export function decidePlaybackSource(input: PlaybackDecisionInput): PlaybackDecision {
  if (input.offline) return 'download';
  if (!input.downloaded) return 'stream';
  // A download that finishes while its video is already streaming must not
  // swap the player or raise a prompt mid-watch.
  if (!input.downloadedAtOpen) return 'stream';
  if (input.visitChoice) return input.visitChoice;
  if (input.preference === null) return 'pending';
  return input.preference === 'ask' ? 'choose' : input.preference;
}

/** Test-only: reset in-memory state. */
export function __resetPlaybackPreferenceForTests(): void {
  preference = DEFAULT_PREFERENCE;
  loaded = false;
  loadPromise = null;
  listeners.clear();
}
